import { AlertCircle, Check, CheckCircle2, Copy, Info } from 'lucide-react';
import { ReactNode, useState } from 'react';

export const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="mb-5 md:mb-6">
    <h2 className="text-xl md:text-2xl font-semibold">{title}</h2>
    {subtitle && <p className="mt-2 text-sm md:text-base text-lend-body">{subtitle}</p>}
  </div>
);

/** The light-blue box with a bold amount (eligible amount, amount to pay). */
export const AmountBanner = ({ label, amount }: { label: string; amount: string }) => (
  <div className="rounded-lg bg-lend-blue-soft px-4 py-5 text-center mb-6">
    <span className="text-sm md:text-base text-lend-body">{label}: </span>
    <span className="text-xl md:text-2xl font-semibold text-lend-blue-dark">{amount}</span>
  </div>
);

export interface Row {
  label: string;
  value: ReactNode;
}

/** Label/value rows under a small heading (Purchase Breakdown, Loan Terms, Payment Summary). */
export const DetailRows = ({ title, rows, children }: { title?: string; rows: Row[]; children?: ReactNode }) => (
  <section className="py-4 border-b border-lend-line last:border-0">
    {title && <h3 className="text-sm md:text-base font-medium mb-3">{title}</h3>}
    {children}
    <dl className="space-y-2.5">
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-4 text-sm">
          <dt className="text-lend-body">{row.label}</dt>
          <dd className="font-medium text-right">{row.value}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export const Notice = ({ tone = 'error', children }: { tone?: 'error' | 'info'; children: ReactNode }) => (
  <div
    role={tone === 'error' ? 'alert' : 'status'}
    className={`flex gap-2 rounded-md px-3 py-2.5 text-sm mb-5 ${tone === 'error' ? 'bg-error-50 text-error-700' : 'bg-lend-blue-soft text-lend-blue-dark'}`}
  >
    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
    <span>{children}</span>
  </div>
);

/** Payment Successful and the other outcome screens: tinted icon circle, title, subtitle, optional details box. */
export const StatusCard = ({
  tone,
  title,
  subtitle,
  children,
}: {
  tone: 'success' | 'info' | 'error';
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;
}) => (
  <div className="flex flex-col items-center text-center py-2">
    <div
      className={`h-20 w-20 md:h-24 md:w-24 rounded-full flex items-center justify-center ${
        tone === 'success' ? 'bg-lend-green-soft' : tone === 'error' ? 'bg-error-50' : 'bg-lend-blue-soft'
      }`}
    >
      <span
        className={`h-11 w-11 md:h-12 md:w-12 rounded-full flex items-center justify-center text-white ${
          tone === 'success' ? 'bg-lend-green' : tone === 'error' ? 'bg-error-600' : 'bg-lend-blue-dark'
        }`}
      >
        {tone === 'error' && <AlertCircle className="h-6 w-6" aria-hidden />}
        {tone === 'info' && <Info className="h-6 w-6" aria-hidden />}
        {tone === 'success' && <Check className="h-6 w-6" strokeWidth={3} aria-hidden />}
      </span>
    </div>
    <h2 className="mt-5 text-xl md:text-2xl font-semibold">{title}</h2>
    {subtitle && <p className="mt-2 text-sm md:text-base text-lend-body max-w-md">{subtitle}</p>}
    {children && <div className="mt-6 w-full max-w-md text-left rounded-lg border border-lend-line px-4">{children}</div>}
  </div>
);

export const Badge = ({ children }: { children: ReactNode }) => (
  <span className="inline-block rounded-full bg-lend-green-soft text-lend-green text-xs font-medium px-2.5 py-1">{children}</span>
);

/** A value with a copy button (payment reference, account number). */
export const Copyable = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      {value}
      <button type="button" onClick={copy} aria-label={`Copy ${value}`} className="text-lend-blue hover:text-lend-blue-dark">
        {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </span>
  );
};

/** The loading animation from design (assets/loading.gif). */
export const Loading = ({ label }: { label?: string }) => (
  <div className="flex flex-col items-center gap-3 py-6" role="status">
    <img src={require('../../assets/loading.gif')} alt="" className="h-12 w-12" />
    {label && <span className="text-sm text-lend-body">{label}</span>}
  </div>
);

export type CheckState = 'done' | 'active' | 'todo';

/** Assessing eligibility: what has happened and what is happening now. */
export const Checklist = ({ items }: { items: { label: string; state: CheckState }[] }) => (
  <ul className="space-y-2.5 inline-flex flex-col items-start">
    {items.map((item) => (
      <li key={item.label} className={`flex items-center gap-2 text-sm md:text-base ${item.state === 'todo' ? 'text-lend-muted' : 'text-lend-ink'}`}>
        {item.state === 'done' && <CheckCircle2 className="h-5 w-5 text-lend-green" aria-hidden />}
        {item.state === 'active' && <span className="h-5 w-5 rounded-full border-2 border-lend-green border-r-transparent animate-spin" aria-hidden />}
        {item.state === 'todo' && <span className="h-5 w-5 rounded-full border-2 border-gray-300" aria-hidden />}
        {item.label}
      </li>
    ))}
  </ul>
);
